import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, InputLabel, FormControl, Alert, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { CouponCodeInterface } from '../../../interfaces/interfaces';

interface Props {
  open: boolean;
  codeToUpdate: CouponCodeInterface | null;
  setCodeToUpdate: React.Dispatch<React.SetStateAction<CouponCodeInterface | null>>;
  onClose: () => void;
  onSave: () => void;
  updateError?: string | null;
  updateSuccess?: string | null;
}

export const CouponCodesUpdateDialog: React.FC<Props> = ({
  open,
  codeToUpdate,
  setCodeToUpdate,
  onClose,
  onSave,
  updateError,
  updateSuccess,
}) => {
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (codeToUpdate) {
      setValidFrom(codeToUpdate.validFrom ? dayjs(codeToUpdate.validFrom) : null);
      setValidUntil(codeToUpdate.validUntil ? dayjs(codeToUpdate.validUntil) : null);
    }
  }, [codeToUpdate]);

  const handleDateUpdate = useCallback(() => {
    if (codeToUpdate) {
      setCodeToUpdate({
        ...codeToUpdate,
        validFrom: validFrom?.toDate() || new Date(),
        validUntil: validUntil?.toDate() || new Date(),
      });
    }
  }, [codeToUpdate, validFrom, validUntil, setCodeToUpdate]);

  useEffect(() => {
    handleDateUpdate();
  }, [validFrom, validUntil, handleDateUpdate]);

  const discountTypeHelperText = codeToUpdate?.discountType === 'percentage' 
    ? 'Enter 10 for 10% discount' 
    : 'Enter actual amount (e.g., 30 for $30 off)';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Update Global Coupon Code</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {updateError && <Alert severity="error">{updateError}</Alert>}
        {updateSuccess && <Alert severity="success">{updateSuccess}</Alert>}
        
        <TextField
          label="Code"
          value={codeToUpdate?.code ?? ''}
          onChange={e => setCodeToUpdate(c => c ? { ...c, code: e.target.value.toUpperCase() } : c)}
          fullWidth
          margin="dense"
          required
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="discount-type-update-label">Discount Type</InputLabel>
          <Select
            labelId="discount-type-update-label"
            value={codeToUpdate?.discountType ?? 'percentage'}
            label="Discount Type"
            onChange={e => setCodeToUpdate(c => c ? { ...c, discountType: e.target.value } : c)}
          >
            <MenuItem value="percentage">Percentage</MenuItem>
            <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Discount Value"
          type="number"
          value={codeToUpdate?.discountValue ?? 0}
          onChange={e => setCodeToUpdate(c => c ? { ...c, discountValue: parseFloat(e.target.value) } : c)}
          fullWidth
          margin="dense"
          required
          helperText={discountTypeHelperText}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Valid From"
              value={validFrom}
              onChange={(newValue) => setValidFrom(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Valid Until"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>

        <TextField
          label="Max Usage"
          type="number"
          value={codeToUpdate?.maxUsage ?? 100}
          onChange={e => setCodeToUpdate(c => c ? { ...c, maxUsage: parseInt(e.target.value) } : c)}
          fullWidth
          margin="dense"
        />

        <TextField
          label="Description"
          value={codeToUpdate?.description ?? ''}
          onChange={e => setCodeToUpdate(c => c ? { ...c, description: e.target.value } : c)}
          fullWidth
          margin="dense"
          multiline
          rows={2}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="is-global-update-label">Is Global</InputLabel>
            <Select
              labelId="is-global-update-label"
              value={codeToUpdate?.isGlobal !== undefined ? (codeToUpdate.isGlobal ? 'yes' : 'no') : 'yes'}
              label="Is Global"
              onChange={e => setCodeToUpdate(c => c ? { ...c, isGlobal: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="auto-apply-update-label">Auto Apply</InputLabel>
            <Select
              labelId="auto-apply-update-label"
              value={codeToUpdate?.autoApply !== undefined ? (codeToUpdate.autoApply ? 'yes' : 'no') : 'no'}
              label="Auto Apply"
              onChange={e => setCodeToUpdate(c => c ? { ...c, autoApply: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="platform-subsidized-update-label">Platform Subsidized</InputLabel>
            <Select
              labelId="platform-subsidized-update-label"
              value={codeToUpdate?.platformSubsidized !== undefined ? (codeToUpdate.platformSubsidized ? 'yes' : 'no') : 'no'}
              label="Platform Subsidized"
              onChange={e => setCodeToUpdate(c => c ? { ...c, platformSubsidized: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="active-update-label">Active</InputLabel>
            <Select
              labelId="active-update-label"
              value={codeToUpdate?.active !== undefined ? (codeToUpdate.active ? 'yes' : 'no') : 'yes'}
              label="Active"
              onChange={e => setCodeToUpdate(c => c ? { ...c, active: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={onSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
